import React, { useContext, useEffect, useMemo, useState } from "react";
import { DebugContext } from "../components/debug/DebugContextProvider";
import {
  PlaceholderGroup,
  PlaceholderGroupDataType,
  PlaceholderGroupMember,
  PlaceholderGroupValidations,
  PluginEnableContext,
} from "./PluginRegistryTypes";
import PluginPlaceholder from "./PluginPlaceholder";
import {
  PlaceholderGroupValidationResult,
  validatePlaceholdersGroup,
} from "./PlaceholderGroupValidations";
import { PluginRegistryContext } from "./PluginRegistryContextProvider";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";
import { AnalyticsContext } from "../contexts/AnalyticsContextProvider";
import { PluginPlaceholderContext } from "./PluginPlaceholderContextProvider";
import { Logger } from "../logging/logger";
import { DebugPluginPlaceholderGroup } from "../components/debug/DebugPluginPlaceholderGroup";
import { useExperimentsContext } from "../contexts/ExperimentsProvider";
import { ProductContext } from "../contexts/ProductContextProvider";

export interface PlaceholderGroupProps<T> {
  placeholderGroupType: PlaceholderGroup;
  dataGroup: PlaceholderGroupDataType<T>[];
  children?: React.ReactNode | React.ReactNode[];
  validationTypes: PlaceholderGroupValidations[];
}

const PluginPlaceholderGroup = <T,>(props: PlaceholderGroupProps<T>) => {
  const { placeholderGroupType, children, dataGroup, validationTypes } = props;
  const { isDebug } = useContext(DebugContext);
  const { pluginRegistry, pluginOrdering } = useContext(PluginRegistryContext);
  const pageProps = useContext(PageStaticDataContext);
  const { experimentImpression } = useContext(AnalyticsContext);
  const { componentNames } = useContext(PluginPlaceholderContext);
  const { useExperiment, isExperimentInitialized } = useExperimentsContext();
  const { clientSideStateInitialized } = useContext(ProductContext);

  const [validationResult, setValidationResult] = useState<
    PlaceholderGroupValidationResult | undefined
  >(undefined);

  useEffect(() => {
    let mounted = true;
    const context: PluginEnableContext = {
      experimentImpression,
      decision: pageProps.decision,
      pageProps,
    };

    if (clientSideStateInitialized && isExperimentInitialized) {
      const result = validatePlaceholdersGroup({
        pluginRegistry,
        enableContext: context,
        parentComponents: componentNames,
        dataGroup,
        validationTypes,
        pluginOrdering,
        useExperiment,
      });

      if (mounted) {
        setValidationResult(result);
        if (!result.passed) {
          Logger.instance.error("PlaceholderGroup validation failed", { result });
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [clientSideStateInitialized, isExperimentInitialized]);

  const placeholders = dataGroup.map((placeholderGroupMember, index) => {
    return (
      <PluginPlaceholder
        placeholderType={placeholderGroupMember.type}
        key={`${placeholderGroupType}-${index}`}
        customData={placeholderGroupMember.data}
      />
    );
  });

  if (placeholders.length) {
    if (isDebug) {
      return (
        <DebugPluginPlaceholderGroup
          placeholderGroupType={placeholderGroupType}
          validationResults={validationResult}
        >
          {placeholders}
        </DebugPluginPlaceholderGroup>
      );
    }
    return <>{placeholders}</>;
  }

  if (isDebug) {
    return (
      <DebugPluginPlaceholderGroup
        placeholderGroupType={placeholderGroupType}
        validationResults={validationResult}
      >
        {children}
      </DebugPluginPlaceholderGroup>
    );
  }

  return <>{children}</>;
};

export default PluginPlaceholderGroup;
